/**
 * AD-2.1 PR1 — `useServerFlag`.
 *
 * Reads a curated SERVER feature flag (from `/api/config`, cached by the
 * boot-time Supabase init) so a single flag governs BOTH the UI affordance and
 * the server accept/persist. This is distinct from `lib/featureFlag.ts`, which
 * holds frontend-only URL/localStorage rollout flags.
 *
 * The flags are populated by the one boot-time `/api/config` fetch. If this
 * hook mounts before that resolves, it awaits the (cached, idempotent)
 * `getSupabaseClient()` and re-reads once — so the value is correct regardless
 * of mount timing. Server flags don't change after boot, so no polling.
 *
 * ⚖️ B149 — AND A FAILED READ IS NOT AN ANSWER. `/api/config` answers 503 while
 * the instance serving it has never completed a `feature_flags` read: no flags
 * map at all, because a map of `false` from an instance that has never heard
 * from the table is a guess. `lib/supabase` keeps asking, bounded; this hook
 * subscribes and reports `ready: false` for the length of that wait, so a
 * caller that redirects on OFF holds rather than acting on a value nobody has
 * said. When the wait ends it settles on the last cached values if this device
 * has them, else on the empty map — the failed state callers already handle.
 *
 * ⚖️ B149 fix-4 (F2) — AND A GIVE-UP IS NOT AN ANSWER EITHER. `ready` alone
 * cannot tell a genuine 200 from a bounded retry that gave up on the empty
 * map (or on a cache) — both settle it. `authoritative` is the narrower
 * signal: true only once a flags map has actually been received. A caller
 * that decides ONCE (Step7Welcome's CTA, the first look's entry effect) reads
 * this before treating `ready` as an answer.
 */

import { useEffect, useState } from 'react';
import {
  areServerFlagsSettled,
  getServerFlags,
  getSupabaseClient,
  isServerFlagsAuthoritative,
  subscribeServerFlags,
} from '../lib/supabase';

export interface ServerFlagState {
  /** The flag value. Fail-closed: unknown/unreachable config reads OFF. */
  enabled: boolean;
  /** True once the boot-time config read has STOPPED ASKING (a flags map
   *  arrived, or the bounded retry gave up), so the `enabled` value is as
   *  known as it is going to get. Callers that redirect on OFF must gate on
   *  this to avoid bouncing during the window when a genuinely-ON flag still
   *  reads false. */
  ready: boolean;
  /**
   * B149 fix-4 — true once a flags map has actually been RECEIVED, not
   * merely given up on. A one-shot decision must not treat `ready: true,
   * authoritative: false` as an answer — see `lib/supabase.ts`.
   *
   * Optional, not because the real hook ever omits it (`readFlagState` below
   * always supplies it), but so an existing fixture typed against this
   * interface elsewhere in the app — a literal `{ enabled, ready }`, not this
   * lane's — keeps compiling: an omitted value reads the same as `false`,
   * which is the fail-closed default this field already has everywhere it
   * matters.
   */
  authoritative?: boolean;
}

/** The flag as this device currently knows it. */
function readFlagState(name: string): ServerFlagState {
  return {
    enabled: Boolean(getServerFlags()[name]),
    ready: areServerFlagsSettled(),
    authoritative: isServerFlagsAuthoritative(),
  };
}

/**
 * Like `useServerFlag`, but also exposes a `ready` signal. Until the boot-time
 * `/api/config` read resolves, `ready` is false and `enabled` is the (possibly
 * stale-empty) cache value — a caller must NOT act on `enabled` until `ready`.
 *
 * B149 — `ready` now tracks the module's settle rather than this hook's own
 * promise, so a 503 that is still being retried reports `ready: false` and a
 * read that lands on the third attempt releases every mounted caller at once.
 */
export function useServerFlagReady(name: string): ServerFlagState {
  const [state, setState] = useState<ServerFlagState>(() => readFlagState(name));

  useEffect(() => {
    let alive = true;
    // Keep the identity when nothing changed: a fresh object on every notify
    // would re-run every consumer's effects (the B99 hook lesson).
    const sync = (): void => {
      if (!alive) return;
      setState((prev) => {
        const next = readFlagState(name);
        return prev.enabled === next.enabled &&
          prev.ready === next.ready &&
          prev.authoritative === next.authoritative
          ? prev
          : next;
      });
    };
    // Re-seed on name change: a new flag is unknown until the module confirms.
    sync();
    const unsubscribe = subscribeServerFlags(sync);
    // getSupabaseClient is cached/idempotent: it starts the boot cycle if this
    // is the first caller, joins the one in flight otherwise, and — since
    // B149 fix-3 (F4) — answers with the failed state, in the same tick and
    // without asking again, once a cycle has given up. Every mount is a
    // caller, so a fresh cycle per mount was the unbounded read count that
    // rule closed. Both settlements — a client, or none at all — leave the
    // flags settled, so both re-read.
    void getSupabaseClient().then(sync, sync);
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [name]);

  return state;
}

export function useServerFlag(name: string): boolean {
  return useServerFlagReady(name).enabled;
}
